import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiImageOutline, mdiFileVideoOutline, mdiFileMusicOutline } from '@mdi/js'
import { PdfPreview } from './PdfPreview'
import { useIsMobile } from '../../hooks/use-mobile'
import { useAuthedObjectUrl } from '../../hooks/useAuthedObjectUrl'
import { ensureAuthCookie, refreshAuthToken } from '../../api/client'
import { PreviewError, PreviewLoading } from './PreviewError'
import { SECTION_ICONS } from '../../lib/section-icons'

// Single mime-category classifier shared by every preview surface (document
// detail page, DocumentPreviewCard). Keep this the one place that maps a
// content-type to a rendering strategy.
export type DocumentPreviewCategory = 'image' | 'pdf' | 'video' | 'audio' | 'unknown'

export function getContentTypeCategory(mimeType: string | undefined | null): DocumentPreviewCategory {
  if (!mimeType) return 'unknown'
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType === 'application/pdf') return 'pdf'
  if (mimeType.startsWith('video/')) return 'video'
  // A recording is a first-class document since spec 2026-09-09-01: a voice
  // message arrives as an audio attachment, and the console has to be able to
  // play it whatever its transcript says.
  if (mimeType.startsWith('audio/')) return 'audio'
  return 'unknown'
}

// getFileTypeIcon returns the MDI path representing a document's mime
// category — used for header rows / list rows across the app.
export function getFileTypeIcon(mimeType: string | undefined | null): string {
  switch (getContentTypeCategory(mimeType)) {
    case 'image':
      return mdiImageOutline
    case 'video':
      return mdiFileVideoOutline
    case 'audio':
      return mdiFileMusicOutline
    default:
      return SECTION_ICONS.documents
  }
}

interface DocumentPreviewProps {
  mimeType: string | undefined | null
  name: string
  contentUrl: string
  /**
   * URL for the `image` category's <img> src — the negotiated rendition
   * endpoint (`…/documents/{uid}/thumb`, no `size` param). The backend
   * decides whether to stream the original bytes or transcode: a
   * browser-safe type (JPEG, PNG, …) still streams as-is, while a
   * HEIC/HEIF/TIFF document is transcoded to a browser-decodable JPEG so the
   * preview doesn't show the broken-image icon in every browser but Safari
   * (spec 2026-09-13-06). Falls back to `contentUrl` when omitted, so the
   * component stays usable standalone / in tests without a live backend
   * negotiation route. `pdf`/`video`/`audio` and any download link always
   * use `contentUrl` — only the image preview goes through this URL.
   */
  previewUrl?: string
  /** Max height in px for the rendered preview surface (image/pdf/video). */
  maxHeight?: number
  /**
   * When true, skip the outer bordered/background wrapper — used when the
   * caller (e.g. DocumentPreviewCard) already provides its own panel chrome
   * so the preview isn't double-boxed.
   */
  bare?: boolean
  /**
   * An audio document's stored text — the transcript of the recording (spec
   * 2026-09-12-02). Rendered under the player when present; there is no
   * "pending" state, because a mail with an audio attachment carries no stored
   * sentinel and the transcription job may legitimately never run.
   */
  transcript?: string | null
}

/**
 * <video>/<audio> stream with range requests, which a blob URL would turn into
 * one full download, so they keep loading from the API by cookie. This waits
 * until ensureAuthCookie() has put a token the server accepts in the cookie,
 * and turns a media error into the error state with a retry that refreshes
 * the token first.
 */
function useCookieMedia() {
  const [ready, setReady] = useState(false)
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    void ensureAuthCookie().then(() => {
      if (!cancelled) setReady(true)
    })
    return () => {
      cancelled = true
    }
  }, [attempt])

  const retry = () => {
    setFailed(false)
    setReady(false)
    refreshAuthToken()
      .catch(() => null)
      .finally(() => setAttempt((n) => n + 1))
  }

  return { ready, failed, attempt, onError: () => setFailed(true), retry }
}

/**
 * Renders a document's content by mime-category (image / pdf / video /
 * unsupported). Extracted from DocumentDetailPage so every preview surface
 * (the detail page itself, DocumentPreviewCard embedded in interventions /
 * expenses / contracts) shares exactly one implementation.
 *
 * Images and desktop PDFs are fetched with the bearer token and shown from a
 * `blob:` URL (spec 2026-10-11-31): an <img>/<iframe> pointed at the API
 * authenticates by cookie, and a stale cookie made the frame show the raw
 * `invalid_token` JSON as if it were the document.
 */
export function DocumentPreview({
  mimeType,
  name,
  contentUrl,
  previewUrl,
  maxHeight = 600,
  bare = false,
  transcript,
}: DocumentPreviewProps) {
  const { t } = useTranslation('documents')
  const category = getContentTypeCategory(mimeType)
  // pdf case only: desktop keeps the native <iframe> viewer (the user found
  // pdf.js a regression there after seeing both live), mobile gets pdf.js —
  // mobile browsers have no PDF plugin to render a framed document, so the
  // iframe rendered a blank maxHeight-tall box there (spec 2026-09-16-05,
  // overridden after review to a per-viewport strategy instead of one
  // strategy everywhere). useIsMobile() is correct on first paint (see its
  // own comment), so this never flashes one viewer then swaps to the other.
  const isMobile = useIsMobile()

  // The URL this preview loads through the authenticated fetch, if any.
  const blobSource =
    category === 'image' ? previewUrl || contentUrl
      : category === 'pdf' && !isMobile ? contentUrl
        : null
  const { state: blob, retry: retryBlob } = useAuthedObjectUrl(blobSource, category === 'pdf' ? mimeType : null)
  const media = useCookieMedia()

  const wrap = (children: React.ReactNode, extraClass = '') =>
    bare ? <>{children}</> : (
      <div className={`border border-border rounded-lg bg-card ${extraClass}`}>{children}</div>
    )

  const blobGate = (): React.ReactNode | null => {
    if (blob.status === 'error') return wrap(<PreviewError contentUrl={contentUrl} name={name} onRetry={retryBlob} />)
    if (blob.status !== 'ready') return wrap(<PreviewLoading />)
    return null
  }

  const mediaState = (): React.ReactNode | null => {
    if (media.failed) return <PreviewError contentUrl={contentUrl} name={name} onRetry={media.retry} />
    if (!media.ready) return <PreviewLoading />
    return null
  }

  const mediaGate = (): React.ReactNode | null => {
    const state = mediaState()
    return state ? wrap(state) : null
  }

  switch (category) {
    case 'image': {
      const gate = blobGate()
      if (gate || blob.status !== 'ready') return gate
      return wrap(
        <img
          src={blob.objectUrl}
          data-source={blobSource ?? undefined}
          data-testid="document-preview-image"
          alt={name}
          className="max-w-full h-auto rounded-lg shadow-sm"
          style={{ maxHeight }}
        />,
        'p-4',
      )
    }

    case 'pdf': {
      if (isMobile) {
        return wrap(<PdfPreview contentUrl={contentUrl} name={name} maxHeight={maxHeight} />)
      }
      const gate = blobGate()
      if (gate || blob.status !== 'ready') return gate
      return wrap(
        <iframe
          src={blob.objectUrl}
          data-source={contentUrl}
          data-testid="document-preview-iframe"
          className="w-full rounded-lg"
          style={{ height: maxHeight }}
          title={name}
        />,
      )
    }

    case 'video': {
      const gate = mediaGate()
      if (gate) return gate
      return wrap(
        <video
          key={media.attempt}
          src={contentUrl}
          controls
          className="w-full rounded-lg"
          style={{ maxHeight }}
          onError={media.onError}
          data-testid="document-preview-video"
        >
          {t('detail.video_unsupported')}
        </video>,
        'p-4',
      )
    }

    case 'audio': {
      // The transcript is the document's text, not the player's: it stays
      // while the file loads and when the browser cannot play it.
      const state = mediaState()
      return wrap(
        <>
          {state ?? (
            <audio
              key={media.attempt}
              src={contentUrl}
              controls
              className="w-full"
              onError={media.onError}
              data-testid="document-preview-audio"
            >
              {t('detail.audio_unsupported', { defaultValue: t('detail.preview_unavailable') })}
            </audio>
          )}
          {transcript?.trim() ? (
            <p
              className="mt-2 whitespace-pre-wrap text-xs text-muted-foreground"
              data-testid="document-preview-transcript"
            >
              {transcript}
            </p>
          ) : null}
        </>,
        'p-4',
      )
    }

    default:
      return (
        <div className="bg-muted/50 border border-border rounded-lg p-4">
          <p className="text-muted-foreground">{t('detail.preview_unavailable')}</p>
        </div>
      )
  }
}
